import { useUIStore } from '@/store/ui';

const COOLDOWN_MS = 8000;
const SOUND_KINDS = new Set(['done', 'idle', 'completed', 'login', 'approval', 'question', 'credits']);
let context = null;
let lastPlayedAt = -Infinity;

function enabled() {
  return useUIStore.getState().workNotificationSounds !== false;
}

// Create/resume only during a gesture. Never queue a delayed notification
// behind the browser's autoplay gate.
export async function unlockWorkNotificationSound() {
  if (typeof window === 'undefined' || !enabled()) return false;
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return false;
    if (!context || context.state === 'closed') context = new AudioContext();
    if (context.state === 'suspended') await context.resume();
    return context.state === 'running';
  } catch { return false; }
}

export function initWorkNotificationSound() {
  if (typeof document === 'undefined') return () => {};
  const unlock = () => { void unlockWorkNotificationSound(); };
  document.addEventListener('pointerdown', unlock, { passive: true });
  document.addEventListener('keydown', unlock);
  return () => {
    document.removeEventListener('pointerdown', unlock);
    document.removeEventListener('keydown', unlock);
    const previous = context;
    context = null;
    if (previous && previous.state !== 'closed') void previous.close().catch(() => {});
  };
}

export function playWorkNotificationSound(kind, { preview = false } = {}) {
  if (!enabled() || !SOUND_KINDS.has(kind) || context?.state !== 'running') return false;
  const now = Date.now();
  if (now - lastPlayedAt < (preview ? 700 : COOLDOWN_MS)) return false;
  try {
    // A quiet ascending major third, with rounded attacks and soft tails.
    // Sine tones avoid the sharp overtones of a typical alert beep.
    [523.25, 659.25].forEach((frequency, index) => {
      const start = context.currentTime + index * 0.13;
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = frequency;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.055, start + 0.025);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.48);
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.onended = () => { oscillator.disconnect(); gain.disconnect(); };
      oscillator.start(start);
      oscillator.stop(start + 0.5);
    });
    lastPlayedAt = now;
    return true;
  } catch { return false; }
}

export async function previewWorkNotificationSound() {
  if (await unlockWorkNotificationSound()) playWorkNotificationSound('done', { preview: true });
}

// Called only for new live events, never the initial history snapshot.
export function soundForWorkEvents(events) {
  if (events.some(event => [
    'browser_login_requested', 'browser_confirmation_requested', 'user_question_requested',
  ].includes(event?.type))) return playWorkNotificationSound('question');
  if (events.some(event => event?.type === 'session_end'
    && ['completed', 'idle', 'done'].includes(event.status))) {
    return playWorkNotificationSound('done');
  }
  return false;
}
